import { z } from 'zod';

import { logger } from '../utils/logger';

import { developmentConfig } from './environments/development';
import { productionConfig } from './environments/production';
import { type ApiConfig, ApiConfigSchema } from './schemas/api';
import { type McpConfig, McpConfigSchema } from './schemas/mcp';
import { type MonitoringConfig, MonitoringConfigSchema } from './schemas/monitoring';

// Main configuration schema
const ConfigSchema = z.object({
  mcp: McpConfigSchema,
  api: ApiConfigSchema,
  monitoring: MonitoringConfigSchema,
});

export type Config = z.infer<typeof ConfigSchema>;

// Environment detection
const isProduction = import.meta.env.PROD;
const isDevelopment = import.meta.env.DEV;
const nodeEnv = import.meta.env.MODE;

// Get the appropriate configuration based on environment
const getEnvironmentConfig = (): Config => {
  logger.debug({ component: 'config' }, 'Loading environment config', {
    isProduction,
    isDevelopment,
    env: {
      ...import.meta.env,
      // biome-ignore lint/complexity/useLiteralKeys: Required for TypeScript index signature
      VITE_SENTRY_DSN: import.meta.env['VITE_SENTRY_DSN'] ? '[MASKED]' : 'Not configured',
    },
  });

  if (isProduction) {
    return productionConfig;
  }
  return developmentConfig;
};

// Validate and get configuration
const getValidatedConfig = (): Config => {
  try {
    logger.debug({ component: 'config' }, 'Getting environment config');
    const config = getEnvironmentConfig();
    logger.debug({ component: 'config' }, 'Raw config', {
      mcp: config.mcp,
      api: config.api,
      monitoring: {
        ...config.monitoring,
        dsn: config.monitoring.dsn ? '[MASKED]' : 'Not configured',
        environment: config.monitoring.environment,
        debug: config.monitoring.debug,
        sampleRate: config.monitoring.sampleRate,
      },
    });

    logger.debug({ component: 'config' }, 'Validating config');
    const validatedConfig = ConfigSchema.parse(config);
    logger.info({ component: 'config' }, 'Config validation successful', {
      monitoring: {
        environment: validatedConfig.monitoring.environment,
        debug: validatedConfig.monitoring.debug,
        sampleRate: validatedConfig.monitoring.sampleRate,
        dsn: validatedConfig.monitoring.dsn ? '[MASKED]' : 'Not configured',
      },
    });
    return validatedConfig;
  } catch (error) {
    logger.error(
      { component: 'config' },
      'Configuration validation failed',
      error instanceof Error ? error : undefined,
      {
        error: error instanceof Error ? error.message : String(error),
      },
    );
    throw error;
  }
};

// Configuration instance
let configInstance: Config | null = null;

// Get configuration
export const getConfig = (): Config => {
  logger.debug({ component: 'config' }, 'getConfig called', {
    instanceExists: !!configInstance,
  });
  if (!configInstance) {
    configInstance = getValidatedConfig();
  }
  return configInstance;
};

// Typed configuration getters
export const getApiConfig = (): ApiConfig => getConfig().api;
export const getMcpConfig = (): McpConfig => getConfig().mcp;
export const getMonitoringConfig = (): MonitoringConfig => getConfig().monitoring;

// Environment helpers
export const getEnvironment = (): {
  current: string;
  isDevelopment: boolean;
  isProduction: boolean;
  nodeEnv: string;
} => ({
  isProduction,
  isDevelopment,
  nodeEnv,
  // @ts-expect-error - Vite requires dot notation for static replacement
  current: import.meta.env.VITE_SENTRY_ENVIRONMENT || (isProduction ? 'production' : 'development'),
});

// Test helper to reset configuration instance
export const resetConfigForTesting = (): void => {
  configInstance = null;
};
